import { FaCampground, FaExternalLinkAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { SiSalesforce } from "react-icons/si";
import { useState } from "react";
import { GiHamburgerMenu } from "react-icons/gi";
import { logout } from "../features/auth/authSlice";
import PropTypes from "prop-types";
import SimpleNav from "./SimpleNav";

function Navbar({ title }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
  };

  const { pathname } = useLocation();
  if (pathname.includes("lesson")) {
    return <SimpleNav title={title} user={user[0]?.instanceUrl} />;
  }

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const hideNav = () => {
    const elem = document.activeElement;
    if (elem) elem.blur();
  };

  let showGlow = pathname !== "/" && pathname.includes("/auth") && user?.length;

  return (
    <div className={`navbar bg-black relative ${showGlow ? "main-nav" : ""}`}>
      <div className="navbar-start lg:pl-8">
        <FaCampground className="inline pr-2 text-3xl" />
        <Link to="/" className="lg:text-lg font-bold align-middle">
          {title}
        </Link>
      </div>
      <div className="navbar-end lg:mr-10">
        {/* Hamburger Menu */}
        <div className="lg:hidden">
          <button
            onClick={toggleMenu}
            className="text-xl hover:text-purple-300"
            aria-label="Toggle menu"
          >
            <GiHamburgerMenu />
          </button>
        </div>

        {/* Desktop Links */}
        <div className={`hidden lg:flex`}>
          <NavLinks />
          {user?.length ? (
            <UserDropdown handleLogout={handleLogout} hideNav={hideNav} />
          ) : (
            <Link className="text-base" to="/auth">
              Login
            </Link>
          )}
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="absolute top-full right-0 bg-black text-white w-full z-50 p-4 shadow-lg lg:hidden">
            <NavLinks closeMenu={() => setIsMenuOpen(false)} />
            {user?.length ? (
              <div className="mt-4">
                <button
                  onClick={handleLogout}
                  className="block w-full text-left py-4 hover:text-purple-300"
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/auth"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full py-4 hover:text-purple-300"
              >
                Login
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const NavLinks = ({ closeMenu }) => (
  <>
    {/* Learn Dropdown */}
    <div className="dropdown dropdown-hover lg:inline-block">
      <div
        tabIndex={0}
        role="button"
        className="block lg:inline text-xs lg:text-base mr-2 lg:mr-5 hover:text-purple-300 py-4 lg:py-0"
      >
        Learn
      </div>
      <ul
        tabIndex={0}
        className="dropdown-content menu bg-black rounded-box z-[1] w-52 p-2 shadow"
      >
        <li onClick={closeMenu}>
          <Link to="/courses" className="hover:text-purple-300">
            All
          </Link>
        </li>
        <li onClick={closeMenu}>
          <Link
            to="/courses/apex-fundamentals"
            className="hover:text-purple-300"
          >
            Apex Fundamentals
          </Link>
        </li>
        <li onClick={closeMenu}>
          <Link to="/courses/deep-dive" className="hover:text-purple-300">
            Deep Dive
          </Link>
        </li>
        <li onClick={closeMenu}>
          <Link
            to="/courses/object-oriented-programming"
            className="hover:text-purple-300"
          >
            Object Oriented Programming
          </Link>
        </li>
        <li onClick={closeMenu}>
          <Link to="/courses/soql-101" className="hover:text-purple-300">
            SOQL 101
          </Link>
        </li>
        <li onClick={closeMenu}>
          <Link to="/courses/dml" className="hover:text-purple-300">
            DML
          </Link>
        </li>
      </ul>
    </div>

    {/* Practice Dropdown */}
    <div className="dropdown dropdown-hover lg:inline-block">
      <div
        tabIndex={0}
        role="button"
        className="block lg:inline text-xs lg:text-base mr-2 lg:mr-5 hover:text-purple-300 py-4 lg:py-0"
      >
        Practice & Projects
      </div>
      <ul
        tabIndex={0}
        className="dropdown-content menu bg-black rounded-box z-[1] w-52 p-2 shadow"
      >
        <li onClick={closeMenu}>
          <Link to="/practice" className="hover:text-purple-300">
            All Practice
          </Link>
        </li>
        <li onClick={closeMenu}>
          <Link to="/projects" className="hover:text-purple-300">
            Projects
          </Link>
        </li>
        <li onClick={closeMenu}>
          <Link to="/simulations" className="hover:text-purple-300">
            Simulations
          </Link>
        </li>
        <li onClick={closeMenu}>
          <Link to="/practice/flow-into-apex" className="hover:text-purple-300">
            Flow into Apex
          </Link>
        </li>
        <li onClick={closeMenu}>
          <Link to="/playground" className="hover:text-purple-300">
            Playground
          </Link>
        </li>
      </ul>
    </div>

    {/* Events and Competitions Dropdown */}
    <div className="dropdown dropdown-hover lg:inline-block">
      <div
        tabIndex={0}
        role="button"
        className="block lg:inline text-xs lg:text-base mr-2 lg:mr-5 hover:text-purple-300 py-4 lg:py-0"
      >
        Events & Contests
      </div>
      <ul
        tabIndex={0}
        className="dropdown-content menu bg-black rounded-box z-[1] w-52 p-2 shadow"
      >
        <li onClick={closeMenu}>
          <Link to="/advent/2025" className="hover:text-purple-300">
            Advent 2025
          </Link>
          <Link to="/advent/2024" className="hover:text-purple-300">
            Advent 2024
          </Link>
          <Link
            to="/advent/apex-week-of-horror"
            className="hover:text-purple-300"
          >
            Apex Week of Horror
          </Link>
        </li>
        <li onClick={closeMenu}>
          <Link to="/compete" className="hover:text-purple-300">
            Compete
          </Link>
        </li>
      </ul>
    </div>

    <Link
      to="/about"
      onClick={closeMenu}
      className="block lg:inline text-xs lg:text-base mr-2 lg:mr-5 hover:text-purple-300 py-4 lg:py-0"
    >
      About
    </Link>

    <a
      href="https://CampLWC.org"
      target="_blank"
      rel="noopener noreferrer"
      onClick={closeMenu}
      className="block lg:inline text-xs lg:text-base mr-2 lg:mr-5 hover:text-purple-300 py-4 lg:py-0"
    >
      CampLWC
      <FaExternalLinkAlt className="inline ml-1.5 text-[0.7em] align-baseline" />
    </a>
  </>
);

const UserDropdown = ({ handleLogout, hideNav }) => (
  <div className="dropdown dropdown-end">
    <div tabIndex={0} role="button" className="mr-3">
      <SiSalesforce color="#009EDB" className="inline pr-2 text-3xl" />
    </div>
    <ul
      tabIndex={0}
      className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-black rounded-box w-52"
    >
      <li onClick={hideNav}>
        <Link className="text-base" to="/auth">
          Profile
        </Link>
      </li>
      <li onClick={handleLogout}>
        <button className="text-base">Logout</button>
      </li>
    </ul>
  </div>
);

Navbar.defaultProps = {
  title: "Camp Apex",
};

Navbar.propTypes = {
  title: PropTypes.string,
};

NavLinks.propTypes = {
  closeMenu: PropTypes.func,
};

UserDropdown.propTypes = {
  handleLogout: PropTypes.func.isRequired,
  hideNav: PropTypes.func.isRequired,
};

export default Navbar;
